"use client";

import { type ModuleCardGrid } from "@lib/types";
import Image from "next/image";
import { useEffect, useState } from "react";

import { ResponsiveImage } from "@/components/shared/ResponsiveImage";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselIndicator,
  CarouselIndicators,
  CarouselItem,
} from "@/components/ui/carousel";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/ui/cn";

import { iconImage } from "../ui/icons";

export function CardGridModule({ module }: { module: ModuleCardGrid }) {
  return (
    <Container
      as="section"
      fullWidthBackground
      className={cn(
        "bg-[linear-gradient(180deg,var(--Color-Neutral-Darkest,#000003)_0%,var(--Color-Indigo-Darkest,#13233D)_50%,#000212_100%)]",
        module.backgroundImage && "relative",
      )}
    >
      {module.backgroundImage && (
        <ResponsiveImage
          src={module.backgroundImage}
          fill
          className="absolute inset-0 object-cover"
        />
      )}
      <div className="flex flex-col gap-8 py-16">
        <div className="max-w-3xl space-y-3">
          {module.eyebrow ? (
            <span className="text-sm font-semibold tracking-wide text-emerald-500 uppercase">
              {module.eyebrow}
            </span>
          ) : null}
          {module.title ? <h3 className="typography-heading-3">{module.title}</h3> : null}
          {module.intro ? <p className="typography-text-medium-normal">{module.intro}</p> : null}
        </div>

        {/* Desktop Grid Layout */}
        <div className="hidden gap-6 lg:grid lg:grid-cols-3">
          {module.cards?.map((card) => (
            <CardGridCard key={card._key} card={card} />
          ))}
        </div>

        {/* Mobile/Tablet Carousel Layout */}
        <div className="lg:hidden">
          <CardGridCarousel cards={module.cards || []} />
        </div>
      </div>
    </Container>
  );
}

function CardGridCard({ card }: { card: NonNullable<ModuleCardGrid["cards"]>[number] }) {
  const iconPosition = (card as { iconPosition?: "left" | "right" }).iconPosition || "left";
  const icon = card.iconName ? iconImage(`Deafult${card.iconName}`) : null;

  return (
    <div
      className={cn(
        "p-padding-element-lg rounded-lg",
        "flex h-full shrink-0 flex-col",
        "min-h-[303px] max-w-[280px]",
        "tablet:max-w-[360px] tablet:min-h-[395px]",
        "desktop:max-w-[430px] desktop:min-h-[460px]",
        "border-indigo-lighter/50 border-3",
        "bg-[linear-gradient(180deg,rgba(189,212,252,0.20)_0%,rgba(178,178,179,0.20)_100%)] bg-blend-soft-light backdrop-blur-xl",
      )}
    >
      <div
        className={cn(
          "pb-padding-section-sm",
          card.icon && "tablet:size-30 desktop:size-40 aspect-square size-20",
          iconPosition === "left"
            ? "mr-auto items-start pr-4 md:pr-8 lg:pr-48"
            : "ml-auto items-end pl-4 md:pl-8 lg:pl-48",
        )}
      >
        {card.icon ? (
          <ResponsiveImage
            src={card.icon}
            width={160}
            height={160}
            className="h-full w-full object-cover"
            imageOptions={{ desktopWidth: 160 }}
            useNextImage
          />
        ) : icon ? (
          <Image src={icon} alt="Icon" width={160} height={160} />
        ) : null}
      </div>

      <div className="text-indigo-lighter mt-auto flex flex-col gap-2">
        {card.numeral ? (
          <span className="typography-heading-4 text-indigo-lighter/60">{card.numeral}</span>
        ) : null}
        <h5 className="typography-heading-5">{card.title}</h5>
        <div className="typography-text-medium-normal">{card.body}</div>
      </div>
    </div>
  );
}

function CardGridCarousel({ cards }: { cards: NonNullable<ModuleCardGrid["cards"]> }) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    const updateCurrent = () => {
      setCurrent(api.selectedScrollSnap());
    };

    updateCurrent();
    api.on("select", updateCurrent);

    return () => {
      api.off("select", updateCurrent);
    };
  }, [api]);

  return (
    <div className="flex flex-col gap-6">
      <Carousel
        className="gap-gap-element-sm desktop:gap-0 flex flex-col"
        setApi={setApi}
        opts={{
          align: "start",
          slidesToScroll: 1,
          containScroll: "keepSnaps",
        }}
      >
        <CarouselContent rootClassName="px-padding-global -mx-padding-global">
          {cards.map((card) => (
            <CarouselItem key={card._key} className={cn("not-first:pl-gap-element-md! basis-auto")}>
              <CardGridCard card={card} />
            </CarouselItem>
          ))}
        </CarouselContent>

        {/* Indicators - only visible on mobile/tablet */}
        <CarouselIndicators className="px-padding-global flex justify-start md:px-0">
          {cards.map((_, index) => (
            <CarouselIndicator
              key={index}
              index={index}
              isActive={current === index}
              className={cn(
                "max-w-[30px]",
                current === index ? "bg-emerald-light!" : "bg-neutral-400!",
              )}
            />
          ))}
        </CarouselIndicators>
      </Carousel>
    </div>
  );
}
